if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.addEventListener("click", (event) => {
          const button = event.target.closest("button");
          if (!button) return;
          const mediaToSwitch = button.closest("[data-target]");
          if (!mediaToSwitch) return;

          event.preventDefault();
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        });

        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);

        const isVertical = this.dataset.desktopLayout === 'thumbnail_vertical' && this.mql.matches;

        if (isVertical) {
          const thumbnailList = this.elements.thumbnails.querySelector(".thumbnail-list-vertical");
          if (thumbnailList) {
            const containerHeight = thumbnailList.clientHeight;
            const thumbnailTop = thumbnail.offsetTop;
            const thumbnailHeight = thumbnail.offsetHeight;
            const scrollPosition = thumbnailTop - containerHeight / 2 + thumbnailHeight / 2;
            thumbnailList.scrollTo({
              top: scrollPosition,
              behavior: "smooth",
            });
          }
        } else {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}

class VerticalThumbnailCarousel {
  constructor(container) {
    this.container = container;
    this.thumbnailList = container.querySelector(".thumbnail-list-vertical");
    this.prevButton = container.querySelector(".slider-button--prev");
    this.nextButton = container.querySelector(".slider-button--next");

    if (!this.thumbnailList) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateButtonStates();
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.scrollUp());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.scrollDown());
    }

    this.thumbnailList.addEventListener("scroll", () => this.updateButtonStates(), { passive: true });
  }

  scrollUp() {
    if (this.thumbnailList) {
      this.thumbnailList.scrollBy({
        top: -120,
        behavior: "smooth",
      });
    }
  }

  scrollDown() {
    if (this.thumbnailList) {
      this.thumbnailList.scrollBy({
        top: 120,
        behavior: "smooth",
      });
    }
  }

  updateButtonStates() {
    if (!this.thumbnailList || !this.prevButton || !this.nextButton) return;

    const { scrollTop, scrollHeight, clientHeight } = this.thumbnailList;

    if (scrollTop <= 0) {
      this.prevButton.style.opacity = "0.3";
      this.prevButton.style.pointerEvents = "none";
    } else {
      this.prevButton.style.opacity = "1";
      this.prevButton.style.pointerEvents = "auto";
    }

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      this.nextButton.style.opacity = "0.3";
      this.nextButton.style.pointerEvents = "none";
    } else {
      this.nextButton.style.opacity = "1";
      this.nextButton.style.pointerEvents = "auto";
    }
  }
}

// Initialize vertical thumbnail carousels when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  const verticalCarousels = document.querySelectorAll(
    ".thumbnail-slider-vertical"
  );
  verticalCarousels.forEach((carousel) => {
    new VerticalThumbnailCarousel(carousel);
  });
});

// Reinitialize on theme editor changes (for Shopify admin)
document.addEventListener("shopify:section:load", function (event) {
  const verticalCarousels = event.target.querySelectorAll(
    ".thumbnail-slider-vertical"
  );
  verticalCarousels.forEach((carousel) => {
    new VerticalThumbnailCarousel(carousel);
  });
});

// Handle keyboard navigation
document.addEventListener("keydown", function (e) {
  const activeCarousel = document.querySelector(
    ".thumbnail-slider-vertical:focus-within"
  );
  if (!activeCarousel) return;

  const activeThumbnail = activeCarousel.querySelector(
    '.thumbnail-vertical[aria-current="true"]'
  );
  if (!activeThumbnail) return;

  const thumbnails = Array.from(
    activeCarousel.querySelectorAll(".thumbnail-vertical")
  );
  const currentIndex = thumbnails.indexOf(activeThumbnail);

  let newIndex = currentIndex;

  switch (e.key) {
    case "ArrowUp":
      e.preventDefault();
      newIndex = Math.max(0, currentIndex - 1);
      break;
    case "ArrowDown":
      e.preventDefault();
      newIndex = Math.min(thumbnails.length - 1, currentIndex + 1);
      break;
    case "Home":
      e.preventDefault();
      newIndex = 0;
      break;
    case "End":
      e.preventDefault();
      newIndex = thumbnails.length - 1;
      break;
  }

  if (newIndex !== currentIndex) {
    thumbnails[newIndex].focus();
    thumbnails[newIndex].click();
  }
});
